import ObjectFormComponent from 'cl-form/ObjectFormComponent';
import {ColumnsType} from 'cl-table/redux/actions';
import {getColumnDisplayName} from 'cl-table/VirtualizedHelpers';
import * as React from 'react';
import * as _ from 'lodash';
import {useState, useCallback} from 'react';
import FormDialog, {Fields, FormDialogProps} from 'cl-dialogs/FormDialog';
import connectWithRouter from '../../redux/connectWithRouter';
import FormSelect, {FormSelectProps} from 'cl-form/FormSelect/FormSelect';
import Plus from 'mdi-material-ui/Plus';
import {FormSelectOptions} from 'cl-form/utils/orderByUtils';
import Button, {ExtendedButtonProps} from '@material-ui/core/Button/Button';
import {getFormValues, change} from 'redux-form';
import {createValidateAllObjectRequired} from 'cl-form/validation/validators';
import {extractObjectData} from 'cl-form/utils/objectFormUtils';
import {ExtendedWrappedFieldExternalProps, ExtendedWrappedFieldProps} from 'cl-form/FormFields';

const AddAspectSelectorButton: React.FunctionComponent<ExtendedButtonProps & ExtendedWrappedFieldProps> = ({
  showErrors,
  id,
  ...props
}) => {
  return (
    <Button id={id} {...props}>
      <Plus />
      Add Variation Options
    </Button>
  );
};
const MAX_VARIATION_SPECIFICS = 5;
const FIELD_PREFIX = 'field-';

export type aspectValuesType = {[key: string]: string[]};
export type variationsSpecificSetType = {[key: string]: aspectValuesType};

export interface EditVariationOptionsDialogProps extends FormDialogProps {
  recommendedVariationSpecifics: object;
  initialVariationsSpecificSet: aspectValuesType;
  variationsSpecificSet?: variationsSpecificSetType;
  change: typeof change;
  aspectSelectProps: FormSelectProps;
  columns?: ColumnsType;
}

const EditVariationOptionsDialog: React.FunctionComponent<EditVariationOptionsDialogProps> = ({
  recommendedVariationSpecifics,
  initialVariationsSpecificSet,
  variationsSpecificSet,
  formName,
  change,
  aspectSelectProps,
  columns,
  ...props
}) => {
  const ids = _.isEmpty(initialVariationsSpecificSet)
    ? [_.uniqueId(FIELD_PREFIX)]
    : _.map(initialVariationsSpecificSet, () => _.uniqueId(FIELD_PREFIX));

  const [registeredInputFields, setRegisteredInputFields] = useState(ids);

  const createInitialValues = () => {
    const initialValues = {};
    let index = 0;
    _.forEach(initialVariationsSpecificSet, (fieldValue, fieldName) => {
      initialValues[registeredInputFields[index++]] = JSON.stringify({[fieldName]: fieldValue});
    });
    return initialValues;
  };
  const [initialValues] = useState(createInitialValues());

  const getSelectedAspectName = useCallback(
    _.memoize(index => {
      return variationsSpecificSet && variationsSpecificSet[index] ? _.keys(variationsSpecificSet[index])[0] : '';
    }),
    [variationsSpecificSet]
  );

  const getMappedOptions = useCallback(
    _.memoize(
      (specific: string): FormSelectOptions => {
        if (specific && recommendedVariationSpecifics[specific]) {
          return _.map(recommendedVariationSpecifics[specific].recommendations, (value: string) => ({value}));
        }
        return [];
      }
    ),
    [recommendedVariationSpecifics]
  );

  const getSourceOptions = useCallback(
    _.memoize(
      (idToFill: string): FormSelectOptions => {
        const takenAspects = _.map(_.omit(variationsSpecificSet, idToFill), (_, id) => getSelectedAspectName(id));
        return _.map(_.difference(_.keys(recommendedVariationSpecifics), takenAspects), option => ({
          value: option,
          label: columns ? getColumnDisplayName(option, columns[option]) : option,
        }));
      }
    ),
    [recommendedVariationSpecifics, variationsSpecificSet]
  );

  const onAddAspectSelector = () => {
    setRegisteredInputFields(registeredInputFields => _.concat(registeredInputFields, _.uniqueId(FIELD_PREFIX)));
  };

  const getInputFields = (): ExtendedWrappedFieldExternalProps[] => {
    return _.map(
      registeredInputFields,
      (fieldName, index): ExtendedWrappedFieldExternalProps => {
        return {
          component: ObjectFormComponent,
          name: fieldName,
          props: {
            left: {
              component: FormSelect,
              props: {
                clearable: true,
                options: getSourceOptions(fieldName),
                creatable: true,
                label: 'Variation option',
                placeholder: 'Select or type to add...',
                ...aspectSelectProps,
                id: `cla-variation-option-${index}`,
              },
            },
            right: {
              component: FormSelect,
              props: {
                multi: true,
                fullWidth: true,
                creatable: true,
                options: getMappedOptions(getSelectedAspectName(fieldName)),
                placeholder: 'Select or type to add...',
                label: 'Option values',
                id: `cla-option-values-${index}`,
              },
            },
          },
          onDeleteField: () => {
            change(formName, fieldName, null);
            setRegisteredInputFields(registeredInputFields => _.filter(registeredInputFields, id => id !== fieldName));
          },
        };
      }
    );
  };

  const inputFields = getInputFields();
  return (
    <FormDialog
      formName={formName}
      title="Edit product variations"
      text="Choose up to five variation options for this product to create and manage SKUs and their inventory levels.
      Arrange options here in the order you want them to appear in your listing."
      initialValues={initialValues}
      validate={createValidateAllObjectRequired(inputFields)}
      fields={
        [
          ...inputFields,
          {
            component: AddAspectSelectorButton,
            name: 'field-button',
            id: 'cla-btn-add-variation-option',
            props: {
              onClick: onAddAspectSelector,
              disabled:
                _.size(registeredInputFields) >=
                Math.min(_.size(recommendedVariationSpecifics), MAX_VARIATION_SPECIFICS),
            },
          },
        ] as Fields
      }
      {...props}
    />
  );
};

export default connectWithRouter(
  (state, props) => ({
    variationsSpecificSet: extractObjectData(getFormValues(props.formName)(state), true),
  }),
  {change}
)(EditVariationOptionsDialog);
